import type { SoftwareDto } from '@bk/contracts';
import type { ReactNode } from 'react';
import { errorMessage } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant } from '../../lib/useTenant';
import { Badge, Button, Card, ErrorText, SectionLoader } from '../../ui';
import {
  softwareCategory,
  softwareMode,
  softwareProblems,
  softwareState,
  problemsLabel,
  SoftwareCard,
  useSoftwareList,
  type LastResult,
} from './Software';
import { SourceProgress } from './WorkProgress';

const SOURCE: Record<string, string> = {
  conversazione: 'dalla conversazione',
  upload: 'da un file caricato',
  dichiarato: 'dichiarato',
};
const WEB: Record<string, string> = {
  possibile: 'possibile',
  vietato: 'vietato dalle condizioni d’uso',
  due_passaggi: 'non possibile: verifica in due passaggi',
  captcha: 'non possibile: captcha all’accesso',
  no: 'nessuna area web',
};
const KIND: Record<string, string> = {
  api: 'API',
  sessione_sito: 'dal sito, chiamate interne',
  pagine: 'dal sito, lettura delle pagine',
};
const DOC_SOURCE: Record<string, string> = {
  specifica_api: 'specifica ufficiale delle API',
  documentazione: 'documentazione ufficiale consultata',
  memoria: 'conoscenza generale, da verificare',
};

/** Un riquadro della scheda: titolo con il numero, poi il contenuto. */
function Box({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {count !== undefined && (
          <span className="rounded-md bg-accent/10 px-1.5 py-0.5 text-[11px] font-bold text-accent">
            {count.toLocaleString('it-IT')}
          </span>
        )}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </Card>
  );
}

/** Valori in griglia, come la testata della scheda dell'entità. */
function Values({ items }: { items: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid gap-x-8 gap-y-4 text-sm sm:grid-cols-2 xl:grid-cols-4">
      {items.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-[11px] font-medium uppercase tracking-wide text-ink/45">{label}</dt>
          <dd className="mt-0.5 break-words">
            {value === null || value === undefined || value === '' ? (
              <span className="text-ink/30">—</span>
            ) : (
              value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

const link = (href: string, label: string) => (
  <a className="text-accent hover:underline" href={href} target="_blank" rel="noreferrer">
    {label}
  </a>
);

/**
 * Scheda di un software (ADR 0121): come lo usate, specifiche dalla verifica, collegamento con le ultime letture, errori;
 * sotto, le operazioni (verifica, collega, sincronizza, scollega).
 */
export function SoftwareDetailPage({ id }: { id: string }) {
  const { tenant, isManager } = useTenant();
  const list = useSoftwareList();
  const back = () => navigate(`/t/${tenant.id}/software`);
  if (list.isLoading) return <SectionLoader />;
  const s = list.data?.data.find((x) => x.id === id);
  if (list.error || !s)
    return (
      <Card>
        <ErrorText>
          {list.error ? errorMessage(list.error) : 'Il programma non è più nell’elenco.'}
        </ErrorText>
        <Button variant="ghost" className="mt-2" onClick={back}>
          Torna all’elenco
        </Button>
      </Card>
    );
  const a = s.assessment;
  const c = s.connection;
  const state = softwareState(s);
  const problems = softwareProblems(s);
  const read = Object.entries((c?.last_result ?? {}) as LastResult);

  return (
    <div className="space-y-5">
      {/* Testata: che programma è, di che tipo e se è collegato. */}
      <Card className="p-0">
        <div className="flex flex-wrap items-start gap-3 border-b border-black/5 px-6 py-5">
          <div className="min-w-0 flex-1">
            <button className="text-xs font-medium text-accent hover:underline" onClick={back}>
              ← Software
            </button>
            <h1 className="mt-1 text-xl font-semibold">
              {s.name}
              {s.product && s.product !== s.name && (
                <span className="font-normal text-ink/55"> · {s.product}</span>
              )}
            </h1>
          </div>
          <Badge tone={state.tone}>{state.label}</Badge>
          {problems.length > 0 && (
            <Badge tone={problems.some((p) => p.tone === 'red') ? 'red' : 'amber'}>
              {problemsLabel(problems)}
            </Badge>
          )}
        </div>
        <div className="px-6 py-5">
          <Values
            items={[
              ['Tipologia', softwareCategory(s)],
              ['Produttore', a?.vendor],
              ['Collegamento', softwareMode(s)],
              [
                'Ultima lettura',
                c?.syncing ? 'in corso…' : c?.last_sync_at ? dateTime(c.last_sync_at) : null,
              ],
              [
                'Verifica',
                a
                  ? a.verified
                    ? 'verificato'
                    : `da verificare · confidenza ${a.confidence}`
                  : s.assessing
                    ? 'in corso…'
                    : 'non ancora fatta',
              ],
              ['Verificato il', a ? dateTime(a.assessed_at) : null],
              ['File esportati (30 giorni)', s.uploads_30d || null],
            ]}
          />
        </div>
      </Card>

      <Box title="Come lo usate" count={s.usages.length}>
        {s.usages.length ? (
          <table className="bk-list min-w-max">
            <thead>
              <tr>
                <th>Uso</th>
                <th>Da dove</th>
                <th>Processo</th>
                <th>Dal</th>
              </tr>
            </thead>
            <tbody>
              {s.usages.map((u) => (
                <tr key={u.id}>
                  <td className="whitespace-normal">{u.description ?? u.how}</td>
                  <td>{SOURCE[u.source] ?? u.source}</td>
                  <td>
                    {u.process_key?.replace(/_/g, ' ') ?? <span className="text-ink/30">—</span>}
                  </td>
                  <td>{dateTime(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-ink/55">Nessun uso descritto.</p>
        )}
      </Box>

      <Box title="Specifiche">
        {a ? (
          <div className="space-y-4">
            <Values
              items={[
                [
                  'API',
                  a.has_api
                    ? a.api_docs_url
                      ? link(a.api_docs_url, 'sì, documentazione')
                      : 'sì'
                    : 'no',
                ],
                ['Accesso', a.oauth ? `${a.auth ?? 'OAuth2'} (consenso dal browser)` : a.auth],
                [
                  'Server MCP ufficiale',
                  a.has_official_mcp ? (a.mcp_url ? link(a.mcp_url, 'sì') : 'sì') : 'no',
                ],
                ['Accesso dal sito', WEB[a.web.status] ?? a.web.status],
                ['Si leggono', a.readable.join(', ')],
                ['Si scrivono', a.writable.join(', ')],
                ['Dati per collegarsi', a.connection_fields.map((f) => f.label).join(', ')],
                ['Credenziali inviate a', a.connection_hosts.join(', ')],
                [
                  'Fonte',
                  a.spec_url
                    ? link(a.spec_url, DOC_SOURCE[a.doc_source]!)
                    : DOC_SOURCE[a.doc_source],
                ],
              ]}
            />
            {a.notes && <p className="text-sm text-ink/70">{a.notes}</p>}
            {a.sources.length > 0 && (
              <details className="text-xs text-ink/55">
                <summary className="cursor-pointer">Pagine consultate ({a.sources.length})</summary>
                <ul className="mt-1 space-y-0.5">
                  {a.sources.map((u) => (
                    <li key={u} className="truncate">
                      {link(u, u)}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        ) : (
          <p className="text-sm text-ink/55">
            {s.assessing
              ? 'Verifica in corso: il sistema legge la documentazione ufficiale.'
              : 'Non ancora verificato: le specifiche arrivano dalla verifica.'}
          </p>
        )}
      </Box>

      <Box title="Collegamento" count={read.length || undefined}>
        {c ? (
          <div className="space-y-4">
            <Values
              items={[
                ['Tipo', KIND[c.kind]],
                ['Stato', <Badge tone={state.tone}>{state.label}</Badge>],
                // I collegamenti dal sito si rileggono ogni tot ore; le scritture ci sono solo per loro.
                ...(c.kind === 'api'
                  ? []
                  : ([
                      [
                        'Rilettura',
                        c.sync_hours === 1
                          ? 'ogni ora'
                          : c.sync_hours === 24
                            ? 'una volta al giorno'
                            : `ogni ${c.sync_hours} ore`,
                      ],
                      ['Scritture disponibili', c.writes.map((w) => w.label).join(', ')],
                    ] as Array<[string, ReactNode]>)),
              ]}
            />
            {c.syncing && c.progress && (
              <SourceProgress source={c.progress} title="Sincronizzazione in corso" />
            )}
            {read.length > 0 && (
              <table className="bk-list min-w-max">
                <thead>
                  <tr>
                    <th>Dato letto</th>
                    <th>Righe</th>
                    <th>Allegati</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {read.map(([k, v]) => (
                    <tr key={k}>
                      <td className="font-medium">{k}</td>
                      <td className="tabular-nums">{v.righe.toLocaleString('it-IT')}</td>
                      <td>
                        {v.allegati ? (
                          `${v.allegati.scaricati} letti${v.allegati.da_vedere ? `, ${v.allegati.da_vedere} da aprire` : ''}`
                        ) : (
                          <span className="text-ink/30">—</span>
                        )}
                      </td>
                      <td>
                        {v.incompleto ? (
                          <span className="text-amber-700">fermata al limite di sicurezza</span>
                        ) : (
                          <span className="text-ink/30">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {c.fields_choice?.ok && <p className="text-xs text-ink/55">{c.fields_choice.note}</p>}
          </div>
        ) : (
          <p className="text-sm text-ink/55">
            Non collegato{softwareMode(s) ? `: ${softwareMode(s)}.` : '.'}
          </p>
        )}
      </Box>

      <Box title="Errori e avvisi" count={problems.length}>
        {problems.length ? (
          <ul className="space-y-2 text-sm">
            {problems.map((p, i) => (
              <li key={i} className="flex gap-2">
                <span
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${p.tone === 'red' ? 'bg-red-600' : 'bg-amber-500'}`}
                />
                <span className={p.tone === 'red' ? 'text-red-800' : 'text-amber-900'}>
                  {p.text}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-ink/55">Nessun errore né avviso.</p>
        )}
      </Box>

      {(isManager || s.exploration) && (
        <div>
          <h3 className="mb-2 px-1 text-sm font-semibold">Operazioni</h3>
          <SoftwareCard s={s} operations />
        </div>
      )}
    </div>
  );
}
